export const voiceSchema = {
  "$schema": "https://json-schema.org/draft/2020-12/schema",
  "title": "OPM.js canonical voice v8",
  "type": "object",
  "additionalProperties": false,
  "required": [
    "version",
    "name",
    "algorithm",
    "feedback",
    "modIndex",
    "lfo",
    "ops"
  ],
  "properties": {
    "version": {
      "const": 8
    },
    "name": {
      "type": "string",
      "pattern": "^[a-zA-Z0-9_-]{1,64}$"
    },
    "algorithm": {
      "type": "integer",
      "minimum": 0,
      "maximum": 7
    },
    "feedback": {
      "type": "integer",
      "minimum": 0,
      "maximum": 7
    },
    "modIndex": {
      "type": "number",
      "minimum": 0,
      "maximum": 16
    },
    "pan": {
      "type": "number", "minimum": -1, "maximum": 1,
      "description": "Voice-level stereo offset added to each note's pan and clamped to -1..1; omitted means zero."
    },
    "pitchEnvelope": {
      "type": "object",
      "additionalProperties": false,
      "required": ["a", "d", "r", "initial", "peak", "sustain", "final"],
      "properties": {
        "a": { "type": "number", "minimum": 0, "maximum": 10 },
        "d": { "type": "number", "minimum": 0, "maximum": 10 },
        "r": { "type": "number", "minimum": 0, "maximum": 10 },
        "initial": { "type": "number", "minimum": -4800, "maximum": 4800 },
        "peak": { "type": "number", "minimum": -4800, "maximum": 4800 },
        "sustain": { "type": "number", "minimum": -4800, "maximum": 4800 },
        "final": { "type": "number", "minimum": -4800, "maximum": 4800 }
      }
    },
    "lfo": {
      "type": "object",
      "additionalProperties": false,
      "required": [
        "rate",
        "amDepth",
        "pmDepth",
        "waveform"
      ],
      "properties": {
        "delay": { "type": "number", "minimum": 0, "maximum": 10, "default": 0 },
        "sync": { "type": "string", "enum": ["note", "global"], "default": "note" },
        "phase": { "type": "number", "minimum": 0, "maximum": 1, "default": 0 },
        "amTargets": {
          "type": "array", "minItems": 4, "maxItems": 4,
          "items": { "type": "number", "minimum": 0, "maximum": 1 },
          "description": "Per-operator AM depth multipliers; omitted means all one."
        },
        "pmTargets": {
          "type": "array", "minItems": 4, "maxItems": 4,
          "items": { "type": "number", "minimum": 0, "maximum": 1 },
          "description": "Per-operator PM depth multipliers; omitted means all one."
        },
        "waveform": {
          "type": "string",
          "enum": ["sine", "triangle", "saw", "square", "noise"],
          "description": "noise holds a deterministic pseudo-random level in -1..1 for each LFO cycle."
        },
        "rate": {
          "type": "number",
          "minimum": 0,
          "maximum": 20
        },
        "amDepth": {
          "type": "number",
          "minimum": 0,
          "maximum": 1
        },
        "pmDepth": {
          "type": "number",
          "minimum": 0,
          "maximum": 1200
        }
      }
    },
    "ops": {
      "type": "array",
      "minItems": 4,
      "maxItems": 4,
      "items": {
        "type": "object",
        "additionalProperties": false,
        "required": [
          "ratio",
          "level",
          "detune",
          "adsr"
        ],
        "properties": {
          "waveform": { "type": "string", "enum": ["sine", "half", "abs", "quarter", "alternating", "camel", "square", "saw", "noise"], "default": "sine" },
          "noiseRate": { "type": "number", "minimum": 20, "maximum": 20000, "default": 8000, "description": "Noise hold clock in Hz; ignored for periodic waveforms." },
          "frequency": {
            "type": "number", "minimum": 1, "maximum": 20000,
            "description": "Fixed Hz before detune, live pitch, pitch envelope and LFO PM; overrides note times ratio."
          },
          "rateKeyScale": {
            "type": "number", "minimum": 0, "maximum": 4,
            "description": "Multiply envelope seconds by 2^(-rateKeyScale*(note-60)/12), capped at 10 seconds (100 for sr)."
          },
          "velocitySensitivity": {
            "type": "number",
            "minimum": 0,
            "maximum": 48,
            "description": "Operator attenuation in dB at velocity zero; omitted means zero."
          },
          "keyScale": {
            "type": "object",
            "additionalProperties": false,
            "required": ["breakpoint", "leftDbPerOctave", "rightDbPerOctave"],
            "properties": {
              "breakpoint": { "type": "integer", "minimum": 0, "maximum": 127 },
              "leftDbPerOctave": { "type": "number", "minimum": 0, "maximum": 24 },
              "rightDbPerOctave": { "type": "number", "minimum": 0, "maximum": 24 }
            }
          },
          "ratio": {
            "type": "number",
            "minimum": 0.125,
            "maximum": 32
          },
          "level": {
            "type": "number",
            "minimum": 0,
            "maximum": 1
          },
          "detune": {
            "type": "number",
            "minimum": -1200,
            "maximum": 1200
          },
          "adsr": {
            "type": "object",
            "additionalProperties": false,
            "required": [
              "a",
              "d",
              "s",
              "r"
            ],
            "properties": {
              "a": {
                "type": "number",
                "minimum": 0,
                "maximum": 10
              },
              "d": {
                "type": "number",
                "minimum": 0,
                "maximum": 10
              },
              "s": {
                "type": "number",
                "minimum": 0,
                "maximum": 1
              },
              "r": {
                "type": "number",
                "minimum": 0,
                "maximum": 10
              },
              "sr": {
                "type": "number",
                "minimum": 0.01,
                "maximum": 100,
                "description": "Sustain rate: seconds for the held level to fall the full 96 dB after decay; omitted holds the sustain level until release."
              }
            }
          }
        }
      }
    }
  }
};
